/**
 * Hook: useFieldBindings
 *
 * Provides step-aware helpers for syncing form data to/from Effector stores
 * via the field binding registry.
 */
import { useCallback, useMemo } from "react";
import { resolveBinding, type FieldBinding } from "./field-binding-registry";
import {
  readStorePath,
  storeAccessors,
  type StoreName,
} from "./store-accessors";

/**
 * Sets a dot-notated path on an object, creating intermediate objects as needed.
 * Returns a new object (does not mutate the original).
 */
const setNestedPath = (
  obj: Record<string, unknown>,
  path: string,
  value: unknown,
): Record<string, unknown> => {
  const result = { ...obj };
  const segments = path.split(".");
  let current: any = result;

  for (let i = 0; i < segments.length - 1; i++) {
    const seg = segments[i];
    current[seg] = current[seg] != null ? { ...current[seg] } : {};
    current = current[seg];
  }

  current[segments[segments.length - 1]] = value;
  return result;
};

export const useFieldBindings = (stepId: string) => {
  /**
   * Syncs all form fields to their bound Effector stores.
   *
   * Groups writes by store to minimize event dispatches.
   */
  const syncToStores = useCallback(
    (formData: Record<string, unknown>) => {
      // Group resolved values by store
      const storePayloads: Record<StoreName, Record<string, unknown>> = {
        account: {},
        quote: {},
        appointment: {},
        bookingProcess: {},
        serviceLocation: {},
      };
      const apiMethodCalls: Array<{
        store: StoreName;
        method: string;
        value: unknown;
      }> = [];

      for (const [fieldName, rawValue] of Object.entries(formData)) {
        const binding = resolveBinding(stepId, fieldName);
        if (!binding) continue;

        const value = binding.transform
          ? binding.transform(rawValue, formData)
          : rawValue;

        // Skip undefined values: avoid overwriting existing store data when a field
        // was never interacted with (e.g. navigating back through a step without touching it).
        // Empty string, null, false, and 0 are intentional clears and should be written.
        if (value === undefined) continue;

        if (binding.apiMethod) {
          // Direct API method call (e.g., setSelectedServices, setBookingType)
          apiMethodCalls.push({
            store: binding.store,
            method: binding.apiMethod,
            value,
          });
        } else {
          // Group into store payloads for batch `set()` calls
          storePayloads[binding.store] = setNestedPath(
            storePayloads[binding.store],
            binding.path,
            value,
          );
        }
      }

      // Dispatch grouped set() calls
      for (const [storeName, payload] of Object.entries(storePayloads) as [
        StoreName,
        Record<string, unknown>,
      ][]) {
        if (Object.keys(payload).length > 0) {
          storeAccessors[storeName].api.set(payload);
        }
      }

      // Dispatch direct API method calls
      for (const { store, method, value } of apiMethodCalls) {
        const api = storeAccessors[store].api;
        if (typeof api[method] === "function") {
          api[method](value);
        }
      }
    },
    [stepId],
  );

  /**
   * Reads initial form values from stores for the given field names.
   */
  const readFromStores = useCallback(
    (fieldNames: string[]): Record<string, unknown> => {
      const values: Record<string, unknown> = {};

      for (const fieldName of fieldNames) {
        const binding = resolveBinding(stepId, fieldName);
        if (!binding) continue;
        const raw = readStorePath(binding.store, binding.path);
        values[fieldName] = binding.readTransform
          ? binding.readTransform(raw)
          : raw;
      }

      return values;
    },
    [stepId],
  );

  /**
   * Gets the binding for a specific field (useful for conditional logic).
   */
  const getBinding = useCallback(
    (fieldName: string): FieldBinding | undefined => {
      return resolveBinding(stepId, fieldName);
    },
    [stepId],
  );

  return useMemo(
    () => ({ syncToStores, readFromStores, getBinding }),
    [syncToStores, readFromStores, getBinding],
  );
};
